import { useTranslation } from 'react-i18next';
import { cn } from '@parrot-software/utils';
import { ClabeDisplay } from '@subscriptions/presentation/components/base/ClabeDisplay/ClabeDisplay';

interface SpeiPaymentMethodDetailProps {
  providerPublicId: string;
  className?: string;
}

/**
 * SpeiPaymentMethodDetail - SPEI payment method summary shared by the block
 * detail and the subscriptions list.
 *
 * Renders the formatted CLABE (with the copy action) plus the bank and
 * recipient rows inside the same bordered box. Bank and recipient are i18n
 * constants (`paymentMethod.bankName` / `paymentMethod.recipientName`) — they
 * do not come from the API.
 *
 * @param {Object} props - Component properties
 * @param {string} [props.className] - Extra classes merged into the CLABE box (e.g. width overrides)
 * @param {string} props.providerPublicId - SPEI CLABE exposed by the payment method
 * @returns {React.ReactElement} SPEI payment method detail box
 *
 * @example
 * <SpeiPaymentMethodDetail providerPublicId={paymentMethod.providerPublicId} />
 */
export const SpeiPaymentMethodDetail = ({ className, providerPublicId }: SpeiPaymentMethodDetailProps) => {
  const { t } = useTranslation('subscriptions');

  return (
    <ClabeDisplay className={cn('font-sans', className)} providerPublicId={providerPublicId}>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm leading-5 text-muted-foreground">
        <div className="flex gap-1">
          <span>{t('paymentMethod.bank')}</span>
          <span className="font-semibold text-foreground">{t('paymentMethod.bankName')}</span>
        </div>
        <div className="flex gap-1">
          <span>{t('paymentMethod.recipient')}</span>
          <span className="font-semibold text-foreground">{t('paymentMethod.recipientName')}</span>
        </div>
      </div>
    </ClabeDisplay>
  );
};
